import styled from "styled-components";
import { Button } from "components";
import { ProctorStatus } from "hooks/useProctoring";
import { ProctorAttemptsResponse } from "api/ProctorAPI";

interface ProctorReadinessProps {
	status: ProctorStatus;
	attempts: ProctorAttemptsResponse | null;
	error: string | null;
	onStartProctoring: () => void;
	isLoading?: boolean;
}

const ProctorReadiness = ({
	status,
	attempts,
	error,
	onStartProctoring,
	isLoading = false,
}: ProctorReadinessProps) => {
	return (
		<Container>
			<Title>Экзамен с прокторингом</Title>
			
			<InfoBlock>
				<InfoTitle>Требования для сдачи:</InfoTitle>
				<RequirementsList>
					<RequirementItem>
						<CheckIcon>✓</CheckIcon>
						<span>Паспорт гражданина РФ</span>
					</RequirementItem>
					<RequirementItem>
						<CheckIcon>✓</CheckIcon>
						<span>Работающая веб-камера</span>
					</RequirementItem>
					<RequirementItem>
						<CheckIcon>✓</CheckIcon>
						<span>Работающий микрофон</span>
					</RequirementItem>
					<RequirementItem>
						<CheckIcon>✓</CheckIcon>
						<span>Отсутствие посторонних лиц в помещении</span>
					</RequirementItem>
					<RequirementItem>
						<CheckIcon>✓</CheckIcon>
						<span>Стабильное интернет-соединение</span>
					</RequirementItem>
				</RequirementsList>
			</InfoBlock>

			<WarningBlock>
				<WarningIcon>⚠️</WarningIcon>
				<WarningText>
					Во время экзамена будет вестись видеозапись с камеры и экрана.
					Нарушения правил могут привести к аннулированию результата.
				</WarningText>
			</WarningBlock>

			{attempts && (
				<AttemptsInfo>
					Осталось попыток: {attempts.remaining_attempts} из{" "}
					{attempts.total_attempts}
				</AttemptsInfo>
			)}

			{error && <ErrorText>{error}</ErrorText>}

			<StartButton
				onClick={onStartProctoring}
				disabled={isLoading || status === "initializing"}
			>
				{status === "initializing" ? "Подготовка..." : "Готов к сдаче с прокторингом"}
			</StartButton>

			<HintText>
				После нажатия начнётся проверка оборудования и идентификация личности
			</HintText>
		</Container>
	);
};

export default ProctorReadiness;

const Container = styled.div`
	max-width: 600px;
	margin: 40px auto;
	padding: 32px;
	background: ${({ theme }) => theme.colors.primaryWhite};
	border-radius: 16px;
	box-shadow: 0 4px 20px ${({ theme }) => theme.colors.shadowPaperContainer};
`;

const Title = styled.h2`
	font-size: 1.5rem;
	font-weight: 600;
	text-align: center;
	margin: 0 0 24px;
	color: ${({ theme }) => theme.colors.primaryBlack};
`;

const InfoBlock = styled.div`
	background: ${({ theme }) => theme.colors.opacityLightGrey10};
	border-radius: 12px;
	padding: 20px;
	margin-bottom: 20px;
`;

const InfoTitle = styled.h3`
	font-size: 1rem;
	font-weight: 600;
	margin: 0 0 16px;
	color: ${({ theme }) => theme.colors.primaryBlack};
`;

const RequirementsList = styled.ul`
	list-style: none;
	padding: 0;
	margin: 0;
`;

const RequirementItem = styled.li`
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 8px 0;
	color: ${({ theme }) => theme.colors.primaryBlack};
	font-size: 0.95rem;
`;

const CheckIcon = styled.span`
	color: ${({ theme }) => theme.colors.primaryGreen};
	font-weight: bold;
	font-size: 1.1rem;
`;

const WarningBlock = styled.div`
	display: flex;
	align-items: flex-start;
	gap: 12px;
	background: ${({ theme }) => theme.colors.opacityYellow10};
	border: 1px solid ${({ theme }) => theme.colors.secondaryYellow};
	border-radius: 12px;
	padding: 16px;
	margin-bottom: 20px;
`;

const WarningIcon = styled.span`
	font-size: 1.2rem;
	flex-shrink: 0;
`;

const WarningText = styled.p`
	margin: 0;
	font-size: 0.9rem;
	color: ${({ theme }) => theme.colors.secondaryGrey};
	line-height: 1.5;
`;

const AttemptsInfo = styled.p`
	text-align: center;
	font-size: 0.95rem;
	color: ${({ theme }) => theme.colors.secondaryGrey};
	margin-bottom: 20px;
`;

const ErrorText = styled.p`
	text-align: center;
	color: ${({ theme }) => theme.colors.secondaryRed};
	font-size: 0.9rem;
	margin-bottom: 16px;
`;

const StartButton = styled(Button)`
	display: block;
	width: 100%;
	height: 48px;
	font-size: 1rem;
	font-weight: 600;
	background: ${({ theme }) => theme.colors.primaryGreen};
	border: none;
	border-radius: 8px;
	color: #fff;
	cursor: pointer;
	transition: background 0.2s;

	&:hover:not(:disabled) {
		background: ${({ theme }) => theme.colors.primaryGreenHover};
	}

	&:disabled {
		background: ${({ theme }) => theme.colors.secondaryLightGray};
		cursor: not-allowed;
	}
`;

const HintText = styled.p`
	text-align: center;
	font-size: 0.85rem;
	color: ${({ theme }) => theme.colors.secondaryGrey};
	margin-top: 12px;
`;






